/**
 * Stripe card payment form (must be rendered inside a Stripe Elements
 * provider). Shows a CardElement, the total due including platform fee
 * (calculateTotalWithFee(amount)), and cancel/pay buttons; on submit it creates
 * a Stripe PaymentMethod and hands the id to onPaymentMethodCreated, showing
 * processing and success overlays. Validation and Stripe errors surface via
 * ErrorContext. Used on SubmissionDisplay when a bounty owner accepts and pays
 * for a submission.
 */
import React, { useState, useEffect } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import LoadingSpinner from "./LoadingSpinner";
import { calculateTotalWithFee } from "@/utils/submission";
import { StripeCardElement } from "@stripe/stripe-js";
import { useError } from "@/contexts/ErrorContext";
import { HandlePaymentMethodCreatedResult } from "@/pages/submission/handlePaymentMethodCreated";

interface PaymentMethodFormProps {
    onPaymentMethodCreated: (paymentMethodId: string) => Promise<HandlePaymentMethodCreatedResult>;
    onCancel: () => void;
    amount: number;
    className?: string;
}

export const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
    onPaymentMethodCreated,
    onCancel,
    amount,
    className,
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const { showError } = useError();
    const [processing, setProcessing] = useState(false);
    const [succeeded, setSucceeded] = useState(false);
    const [isStripeReady, setIsStripeReady] = useState(false);
    const [cardComplete, setCardComplete] = useState(false);

    useEffect(() => {
        if (stripe && elements) {
            setIsStripeReady(true);
        }
    }, [stripe, elements]);

    const resetProcessingAndReturn = (errorMessage: string) => {
        showError(errorMessage);
        setProcessing(false);
        return;
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        if (!stripe || !elements) {
            resetProcessingAndReturn("payment system is not ready. please try again.");
            return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            resetProcessingAndReturn("card element not found. please refresh and try again.");
            return;
        }

        if (!cardComplete) {
            resetProcessingAndReturn("please complete your card information.");
            return;
        }

        setProcessing(true);

        try {
            const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
                type: "card",
                card: cardElement as StripeCardElement,
            });

            if (stripeError) {
                console.error("Stripe error:", stripeError);
                showError(stripeError.message || "an error occurred while processing your payment");
                setProcessing(false);
                return;
            }

            if (paymentMethod) {
                try {
                    const result = await onPaymentMethodCreated(paymentMethod.id);
                    if (result.success) setSucceeded(true);
                } catch (callbackError) {
                    setProcessing(false);
                } finally {
                    setProcessing(false);
                }
            }
        } catch (err) {
            console.error("Payment method creation error:", err);
            showError("an unexpected error occurred. please try again.");
            setProcessing(false);
        }
    };

    const handleCardChange = (event: any) => {
        setCardComplete(event.complete);
        if (event.error) {
            showError(event.error.message);
        }
    };

    if (!isStripeReady) {
        return (
            <div className="flex justify-center items-center p-8">
                <LoadingSpinner message="loading payment system..." />
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className={`space-y-4 ${className}`}>
            <div className="p-4 border border-gray-200 rounded-md font-porsui relative">
                {processing && (
                    <div
                        className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-md"
                        aria-busy="true"
                        aria-live="polite"
                    >
                        <div className="flex items-center space-x-2">
                            <LoadingSpinner message="processing payment..." />
                        </div>
                    </div>
                )}
                {!processing && succeeded && (
                    <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-md">
                        <span className="font-porsui text-base text-black">
                            payment successful!
                        </span>
                    </div>
                )}
                <CardElement
                    onChange={handleCardChange}
                    options={{
                        style: {
                            base: {
                                fontSize: "16px",
                                color: "#424770",
                                "::placeholder": {
                                    color: "#aab7c4",
                                },
                                fontFamily: "Arial, sans-serif",
                            },
                            invalid: {
                                color: "#9e2146",
                            },
                        },
                        hidePostalCode: false,
                    }}
                />
            </div>

            <div className="flex justify-between items-center ml-2">
                <div className="text-gray-600 font-medium">
                    amount to pay: ${calculateTotalWithFee(amount).toFixed(2)}
                </div>
                <div className="space-x-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={processing}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        cancel
                    </button>
                    <button
                        type="submit"
                        disabled={!stripe || processing || !cardComplete}
                        className="px-4 py-2 text-sm font-medium text-white bg-black border border-transparent rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px]"
                    >
                        {processing ? "processing..." : "pay now"}
                    </button>
                </div>
            </div>
        </form>
    );
};
